import { shareImageUrl, shareLinkPath, type SharedResource } from "@/lib/boide-api";

const SITE_ORIGIN = "https://boide.online";

type HeadPayload = {
  meta: Array<Record<string, string>>;
  links: Array<Record<string, string>>;
  scripts: Array<{ type: string; children: string }>;
};

/** Head đầy đủ cho trang chia sẻ: meta OG/Twitter + canonical + JSON-LD.
 * Dùng trong `head()` của route `/q/$token` và `/la-so/$token[/{slug}]`. */
export function buildShareHead(resource: SharedResource | null, token: string): HeadPayload {
  const og = sharedOgMeta(resource);
  const img = token ? shareImageUrl(token, resource?.image_version) : "";
  // URL chuẩn SEO: kèm slug mô tả khi BE đã sinh (share cũ chưa có slug vẫn chạy).
  const canonical = resource
    ? `${SITE_ORIGIN}${shareLinkPath(resource.resource_type, token, resource.slug)}`
    : "";

  const meta: Array<Record<string, string>> = [
    { title: og.title },
    { name: "description", content: og.description },
    { property: "og:title", content: og.title },
    { property: "og:description", content: og.description },
    { property: "og:type", content: "article" },
    { property: "og:site_name", content: "Bói Đê" },
    { name: "twitter:title", content: og.title },
    { name: "twitter:description", content: og.description },
    { name: "twitter:card", content: "summary_large_image" },
  ];
  if (canonical) {
    meta.push({ property: "og:url", content: canonical });
  }
  // Chỉ gắn og:image khi BE đã có ảnh + URL TUYỆT ĐỐI (FB không resolve URL
  // tương đối). Production cần VITE_BOIDE_API_URL=https://api.boide.online.
  // Chưa có ảnh → bỏ qua để dùng og:image brand mặc định của site (đỡ 404).
  if (img && resource?.has_image && /^https?:\/\//.test(img)) {
    meta.push(
      { property: "og:image", content: img },
      { property: "og:image:secure_url", content: img },
      { property: "og:image:type", content: "image/png" },
      { property: "og:image:width", content: "1200" },
      { property: "og:image:height", content: "630" },
      { property: "og:image:alt", content: og.title },
      { name: "twitter:image", content: img },
    );
  }

  const links = canonical ? [{ rel: "canonical", href: canonical }] : [];

  const scripts: Array<{ type: string; children: string }> = [];
  if (resource && canonical) {
    scripts.push({
      type: "application/ld+json",
      children: JSON.stringify({
        "@context": "https://schema.org",
        "@type": "Article",
        headline: og.title,
        description: og.description,
        url: canonical,
        ...(resource.has_image && img ? { image: [img] } : {}),
        publisher: {
          "@type": "Organization",
          name: "Bói Đê",
          url: SITE_ORIGIN,
        },
      }),
    });
  }

  return { meta, links, scripts };
}

/** @deprecated Dùng buildShareHead — giữ lại cho chỗ gọi cũ chỉ cần mảng meta. */
export function buildShareMeta(
  resource: SharedResource | null,
  token: string,
): Array<Record<string, string>> {
  return buildShareHead(resource, token).meta;
}

/** Tiêu đề + mô tả OG cho trang chia sẻ (dùng trong head() của route). */
export function sharedOgMeta(resource: SharedResource | null): {
  title: string;
  description: string;
} {
  if (!resource) {
    return { title: "Link chia sẻ — Bói Đê", description: "Nội dung chia sẻ trên Bói Đê." };
  }
  if (resource.resource_type === "tuvi") {
    const name = resource.data.ho_ten || "Ẩn danh";
    const band = resource.data.scores?.band?.label;
    return {
      title: `Lá số Tử Vi — ${name} | Bói Đê`,
      description: band ? `Tổng quan vận mệnh: ${band}.` : "Lá số Tử Vi luận giải trên Bói Đê.",
    };
  }
  if (resource.resource_type === "bazi") {
    const name = resource.data.ho_ten || "Ẩn danh";
    const dm = resource.data.day_master;
    return {
      title: `Lá số Bát Tự — ${name} | Bói Đê`,
      description: dm?.day_master
        ? `Nhật chủ ${dm.day_master}${dm.ngu_hanh ? ` (${dm.ngu_hanh})` : ""} — luận giải Tứ Trụ trên Bói Đê.`
        : "Lá số Bát Tự / Tứ Trụ luận giải trên Bói Đê.",
    };
  }
  const hex = resource.data.hexagram_result;
  const que = hex.que_chinh?.ten || hex.tieu_de_chuan || "Kinh Dịch";
  return {
    title: `Quẻ ${que} | Bói Đê`,
    description: "Quẻ Kinh Dịch (Mai Hoa Dịch Số) luận giải trên Bói Đê.",
  };
}
